import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap, withLatestFrom } from 'rxjs';

import { BusinessModelApiService } from '@core/data/api/business-model-api.service';
import { catalogPositionFromDto } from '@core/data/api/business-model.mapper';
import { CareerPathwaysApiService } from '@core/data/api/career-pathways-api.service';
import { ladderFromDto, pathwayFromDto } from '@core/data/api/career-pathways.mapper';
import { CatalogPosition, Pathway, PathwaySeat, PersonaLadder } from '@core/models';
import { positionMemberships } from '@core/util/position-grouping.util';
import { DataRepository } from '@core/data/data-repository';
import { CareerPathwaysApiActions, CareerPathwaysPageActions } from './career-pathways.actions';
import { selectPositionCatalog } from './career-pathways.reducer';

/**
 * Loads career-pathway pathways and per-persona ladder placements together, so
 * the screen can resolve a persona's placement against its pathway in one pass.
 */
@Injectable()
export class CareerPathwaysEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly bmApi = inject(BusinessModelApiService);
  private readonly cpApi = inject(CareerPathwaysApiService);
  private readonly store = inject(Store);

  /**
   * The pathways, from the server where there is one; the prototype's seed only
   * without. A failed read is a failure, not an empty list: "there are no
   * pathways" is a claim, and an outage must not make it.
   */
  private pathways$() {
    if (!this.cpApi.available()) return from(this.repo.listPositionPathways());
    return this.cpApi.list().pipe(map((rows): Pathway[] => rows.map(pathwayFromDto)));
  }

  /**
   * Who stands where. From the server, only the placements the caller may see
   * (the server filters); a failed read empties it rather than failing the
   * pathways with it — every screen here still reads without the people on it.
   */
  private ladders$() {
    if (!this.cpApi.available()) return from(this.repo.listPersonaLadders());
    return this.cpApi.allPlacements().pipe(
      map((rows): PersonaLadder[] => rows.map(ladderFromDto)),
      catchError(() => of([] as PersonaLadder[])),
    );
  }

  /**
   * The seats, from the server where there is one.
   *
   * **The ids matter more than the titles.** The catalogue is what the person
   * form, the project wizard, checklists and packets pick a seat from, and what
   * they pick is sent back as a `positionGuid`. Seeded, those ids are `pc-021`,
   * which the server refuses — "is not a valid UUID for positionGuid" — so
   * choosing a seat for somebody could not work at all.
   *
   * The seeded catalogue stays as the fallback, because with no API configured it
   * is the only thing that makes the prototype demo.
   */
  private catalog$() {
    if (!this.bmApi.available()) return from(this.repo.listPositionCatalog());
    return this.bmApi.listPositions().pipe(
      map((rows) => rows.map(catalogPositionFromDto)),
      // A failed read empties it rather than falling back: with a server
      // configured, a seeded seat is a seat that does not exist, and offering one
      // is worse than offering none.
      catchError(() => of([] as CatalogPosition[])),
    );
  }

  /**
   * Persist the catalog whenever a position is created/edited (in the Positions
   * screen) so the change survives the next reload — otherwise re-entering any
   * screen that dispatches `entered` would reload the seed and drop it.
   */
  readonly persistCatalog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CareerPathwaysPageActions.positionUpserted),
        withLatestFrom(this.store.select(selectPositionCatalog)),
        // Skip an empty catalog: that means the store hasn't loaded yet, and
        // persisting [] (or a lone new row) would clobber the seeded catalog.
        switchMap(([, catalog]) =>
          // Only without a server. With one, the seat was already saved by the
          // screen that made it and the next read answers it — writing a copy
          // into the prototype repository would be storing a second truth.
          catalog.length && !this.bmApi.available()
            ? from(this.repo.savePositionCatalog(catalog)).pipe(catchError(() => of(null)))
            : of(null),
        ),
      ),
    { dispatch: false },
  );

  /** On page enter, load pathways, ladders, and the position catalog together. */
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareerPathwaysPageActions.entered),
      switchMap(() =>
        forkJoin({
          pathways: this.pathways$(),
          ladders: this.ladders$(),
          positionCatalog: this.catalog$(),
        }).pipe(
          map((r) =>
            CareerPathwaysApiActions.loadSuccess(
              // The seeded prototype keeps a seat's pathway on the SEAT; the
              // server answers it on the pathway. Without a server, the pathway's
              // seats are read off the seeded catalogue so every screen reads one shape.
              this.cpApi.available() ? r : { ...r, pathways: r.pathways.map((p) => withSeatsFromCatalog(p, r.positionCatalog)) },
            ),
          ),
          catchError((err: unknown) =>
            of(CareerPathwaysApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );
}

/** A seeded pathway, given the seats the seeded catalogue places on it. */
function withSeatsFromCatalog(p: Pathway, catalog: readonly CatalogPosition[]): Pathway {
  const seats: PathwaySeat[] = [];
  for (const pos of catalog) {
    for (const m of positionMemberships(pos)) {
      if (m.pathwayId !== p.id) continue;
      const n = parseInt(String(m.tier).replace(/[^0-9]/g, ''), 10);
      seats.push({ positionId: pos.id, title: pos.title, tier: Number.isFinite(n) ? n : null });
    }
  }
  return { ...p, seats };
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
